import {
  ConfirmationCard,
  ConfirmationEmpty,
  ConfirmationField,
  ConfirmationFields,
  ConfirmationPanel,
} from "@/shared/foundation/primitives";

import type { ConfirmationContentProps } from "./ClientTools";
import styles from "./CreateEventsConfirmation.module.css";

interface EventAssociation {
  dataset_id?: string;
  file_id?: string;
  topic_id?: string;
  message_path_id?: string;
}

interface EventParams {
  name?: string;
  description?: string;
  start_time?: number;
  end_time?: number;
  associations?: EventAssociation[];
  tags?: string[];
  display_options?: { color?: string };
}

/**
 * The subset of `client_create_events`' input this preview reads.
 *
 * Declared here rather than imported from `createEvents.ts`, which imports
 * this component — a shared definition there would be an import cycle
 * (`import/no-cycle` is an error). `CreateEventsInput` is assignable to this,
 * which is what lets `createClientTool` accept this component for that tool
 * and, in doing so, check the two agree.
 */
interface CreateEventsPreviewParams {
  events?: EventParams[];
}

/**
 * Format a nanosecond timestamp into a human-readable datetime string.
 */
function formatNanoseconds(ns: number): string {
  const ms = ns / 1_000_000;
  const date = new Date(ms);

  if (isNaN(date.getTime())) {
    return String(ns);
  }

  return date.toISOString().replace("T", " ").replace("Z", " UTC");
}

/**
 * Summarize associations into a short display string.
 */
function formatAssociations(associations: EventAssociation[]): string {
  return associations
    .map((a) => {
      if (a.dataset_id) {
        return `Dataset: ${a.dataset_id}`;
      }
      if (a.file_id) {
        return `File: ${a.file_id}`;
      }
      if (a.topic_id) {
        return `Topic: ${a.topic_id}`;
      }
      if (a.message_path_id) {
        return `Message Path: ${a.message_path_id}`;
      }
      return "Unknown";
    })
    .join(", ");
}

/**
 * Read-only preview of the events the LLM wants to create.
 * Rendered inline on the ToolApprovalCard and in its details modal.
 */
export function CreateEventsConfirmation({
  params,
}: ConfirmationContentProps<CreateEventsPreviewParams>) {
  const rawEvents: unknown = params.events;
  const events = Array.isArray(rawEvents) ? (rawEvents as EventParams[]) : [];

  if (events.length === 0) {
    return (
      <ConfirmationPanel label="Create events preview">
        <ConfirmationEmpty>No events to create.</ConfirmationEmpty>
      </ConfirmationPanel>
    );
  }

  return (
    <ConfirmationPanel label="Create events preview">
      {events.map((event, index) => (
        <ConfirmationCard
          key={index}
          title={
            <span className={styles.eventName}>
              {event.display_options?.color && (
                <span
                  className={styles.colorSwatch}
                  style={{ backgroundColor: event.display_options.color }}
                />
              )}
              {event.name ?? "Unnamed Event"}
            </span>
          }
        >
          <ConfirmationFields>
            {event.description && (
              <ConfirmationField label="Description">
                {event.description}
              </ConfirmationField>
            )}

            {event.start_time !== undefined && event.end_time !== undefined && (
              <ConfirmationField label="Time">
                {formatNanoseconds(event.start_time)}
                {event.start_time !== event.end_time &&
                  ` → ${formatNanoseconds(event.end_time)}`}
              </ConfirmationField>
            )}

            {event.associations && event.associations.length > 0 && (
              <ConfirmationField label="Associations">
                {formatAssociations(event.associations)}
              </ConfirmationField>
            )}

            {event.tags && event.tags.length > 0 && (
              <ConfirmationField label="Tags">
                {event.tags.join(", ")}
              </ConfirmationField>
            )}
          </ConfirmationFields>
        </ConfirmationCard>
      ))}
    </ConfirmationPanel>
  );
}
